import { Component, OnDestroy, OnInit } from "@angular/core";
import { Router, RouterLink, ActivatedRoute } from "@angular/router";
import { UntypedFormGroup, UntypedFormBuilder, Validators } from "@angular/forms";
import { Observable, Subject } from "rxjs";

import moment from "moment";

import { UnsubscribeService } from "./unsubscribe.service";

import { CommonApiService } from "./../shared/services/api/common-api.service";
import { NotificationsService } from "./../shared/services/notifications.service";
import { ViewStateService } from "./../shared/services/view-state.service";
import { takeUntil } from "rxjs/operators";

@Component({
  selector: "app-unsubscribe",
  templateUrl: "./unsubscribe.component.html",
  styleUrls: ["./unsubscribe.component.css"],
})
export class UnsubscribeComponent implements OnInit, OnDestroy {
  profileForm: UntypedFormGroup;
  profile: any;

  formLevelErrors: String[] = [];

  x = "";
  token = "";
  email = "";
  submitted = false;
  emailOptOut = false;
  emailOptOutCredExp = false;
  emailOptOutAnnual = false;
  emailOptOutCMPAssessment = false;
  emailOptOutMarket = false;
  emailOptOutResearch = false;

  validFormCheck = true;
  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private unsubscribeService: UnsubscribeService,
    private viewState: ViewStateService,
    private router: Router,
    private commonApiService: CommonApiService,
    private notification: NotificationsService
  ) {}

  ngOnInit() {
    this.viewState.startLoading();
    let root = this.route.snapshot.root;
    this.token = root.queryParams["guid"];
    this.x = root.queryParams["x"];
    this.getProfileDetails(this.token, this.x);
    this.buildForm();
  }

  onSubmit(updatedFormValues: any) {
    this.viewState.startLoading();

    this.formValues = updatedFormValues;
    this.validateForm();
    this.updateProfile(this.token, this.x); //token=guid
  }
  cancel(event: Event) {
    event.preventDefault();

    this.profileForm.reset();
    this.router.navigate(["/"]);
  }

  private getProfileDetails(guid: string, email: string) {
    this.unsubscribeService
      .getEmailProfile(guid, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (details) => {
          this.profile = details;
          this.email = details.email;
          this.profile.userId = guid;
          this.profileForm.patchValue(details);
          this.mapBooleanToStingValues(this.profileForm.value);
          this.setOriginalOptOuts(this.profileForm.value);
          this.viewState.stopLoading();
        },
        (err) => {
          this.notification.error(
            "Failed",
            "Record could not be located, please login to update preferences."
          );
          this.viewState.stopLoading();
          this.router.navigate(["/"]);
          console.error(err);
        }
      );
  }

  isEmailOptionChanges(values: any) {
    let changed = false;
    if (
      values.emailOptOut != this.emailOptOut ||
      values.emailOptOutAnnual != this.emailOptOutAnnual ||
      values.emailOptOutCMPAssessment != this.emailOptOutCMPAssessment ||
      values.emailOptOutCredExp != this.emailOptOutCredExp ||
      values.emailOptOutMarket != this.emailOptOutMarket ||
      values.emailOptOutResearch != this.emailOptOutResearch
    )
      changed = true;
    return changed;
  }

  mapBooleanToStingValues(values: any) {
    if (values.emailOptOut) this.profileForm.value.emailOptOut = true;
    else this.profileForm.value.emailOptOut = false;

    if (values.emailOptOutCredExp)
      this.profileForm.value.emailOptOutCredExp = true;
    else this.profileForm.value.emailOptOutCredExp = false;

    if (values.emailOptOutAnnual)
      this.profileForm.value.emailOptOutAnnual = true;
    else this.profileForm.value.emailOptOutAnnual = false;

    if (values.emailOptOutCMPAssessment)
      this.profileForm.value.emailOptOutCMPAssessment = true;
    else this.profileForm.value.emailOptOutCMPAssessment = false;

    if (values.emailOptOutMarket)
      this.profileForm.value.emailOptOutMarket = true;
    else this.profileForm.value.emailOptOutMarket = false;

    if (values.emailOptOutResearch)
      this.profileForm.value.emailOptOutResearch = true;
    else this.profileForm.value.emailOptOutResearch = false;
  }

  setOriginalOptOuts(values: any) {
    this.emailOptOut = values.emailOptOut;
    this.emailOptOutCredExp = values.emailOptOutCredExp;
    this.emailOptOutAnnual = values.emailOptOutAnnual;
    this.emailOptOutCMPAssessment = values.emailOptOutCMPAssessment;
    this.emailOptOutMarket = values.emailOptOutMarket;
    this.emailOptOutResearch = values.emailOptOutResearch;
  }

  private validateForm() {
    for (const control in this.profileForm.controls) {
      this.profileForm.controls[control].updateValueAndValidity();
    }

    let hasErrors = false;
    for (const field in this.formErrors) {
      this.formErrors[field] = "";
      const control = this.profileForm.get(field);
      if (control && control.invalid) {
        hasErrors = true;
        const messages = this.validationMessages[field];
        for (const key in control.errors) {
          this.formErrors[field] += messages[key] + " ";
        }
      }
    }
    if (hasErrors) {
      this.validFormCheck = false;
      this.viewState.stopLoading();
    } else {
      this.validFormCheck = true;
    }
  }

  private buildForm(): void {
    this.profileForm = this.formBuilder.group({
      email: [
        this.formValues.email,
        [Validators.required, Validators.maxLength(100), Validators.email],
      ],
      emailOptOut: [this.formValues.emailOptOut],
      emailOptOutCredExp: [this.formValues.emailOptOutCredExp],
      emailOptOutAnnual: [this.formValues.emailOptOutAnnual],
      emailOptOutCMPAssessment: [this.formValues.emailOptOutCMPAssessment],
      emailOptOutMarket: [this.formValues.emailOptOutMarket],
      emailOptOutResearch: [this.formValues.emailOptOutResearch],
      personId: [this.formValues.personId],
    });
  }

  private updateProfile(guid: string, email: string) {
    if (this.profileForm.valid) {
      this.mapBooleanToStingValues(this.profileForm.value);
      this.unsubscribeService
        .updateEmailProfile(guid, email, this.profileForm.value)
        .pipe(takeUntil(this.destroy$))
        .subscribe(
          (resp) => {
            this.viewState.stopLoading();
            this.notification.success(
              "Success",
              "Email opt-out settings updated."
            );
            this.submitted = true;
          },
          (errors) => {
            this.notification.error(
              "Failed",
              "The email opt-out settings have not been updated."
            );
            this.mapServerErrorsToForm(errors.error);
            this.viewState.stopLoading();
          }
        );
    } else {
      this.viewState.stopLoading();
    }
  }

  private mapServerErrorsToForm(errorObj: any) {
    let errors = errorObj;
    errors.map((error) => {
      this.formErrors[error.fieldName] = "";
      const control = this.profileForm.get(error.fieldName);
      this.formErrors[error.fieldName] += error.message + " ";
      if (!control) {
        this.formLevelErrors.push(error.message);
      }
    });
    this.viewState.stopLoading();
  }

  private formValues = {
    email: "",
    emailOptOut: "",
    emailOptOutCredExp: "",
    emailOptOutCMPAssessment: "",
    emailOptOutAnnual: "",
    emailOptOutMarket: "",
    emailOptOutResearch: "",
    personId: "",
  };

  formErrors = {
    email: "",
    emailOptOut: "",
  };

  validationMessages = {
    email: {
      required: "Email is required.",
      maxlength: "Email must be max 100 characters.",
      email: "Email must be in the correct format.",
    },
  };
  confirmEmailOptOutChanges() {
    if (
      !window.confirm(
        "By clicking ok you are confirming the changes made to your communications preferences and your intent to start and/or stop receiving notices by text/email."
      )
    )
      return false;
    else return true;
  }

  isOptOut(event) {
    if (event.target.checked) {
      if (
        !window.confirm(
          "I acknowledge that choosing “Ok” confirms my intent to stop receiving Credential Maintenance Program notices by email."
        )
      ) {
        this.profileForm.patchValue({
          emailOptOut: false,
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
